import { FC, ReactNode, useEffect, useMemo, useRef, useState } from "react"
import { Fragment } from "react/jsx-runtime"
import AdminPagination from "./AdminPagination";
import { UseAppTableProps } from "hooks/useAppTable";
import AppEmpty from "./AppEmpty";
import { useLocation, useNavigate } from "react-router-dom";
import { parse, stringify } from "qs";


export interface AppTableColumnProps<T> {
    width?: string | number;
    hidden?: boolean;
    key: keyof T;
    label: ReactNode;
    textAlign?: "left" | "center" | "right";
    textAlignHead?: "left" | "center" | "right";
    textAlignBody?: "left" | "center" | "right";
    render?: (row: T) => ReactNode;
    sort?: boolean;
}
interface AppTableProps<T> {
    appTable: UseAppTableProps<T>;
    rowSelection?: boolean;
    columns: Array<AppTableColumnProps<T>>;
    rowClassName?: (row: T) => string;
    cellClassName?: (row: T) => string;
    rowExpandedRender?: (row: T, index: number, pgIndex: number) => ReactNode;
    expandedKeys?: string[];

    onSelect?: (row: T) => void;
    onSelectAll?: () => void;
}
const AppTable = <T extends object>({
    appTable,
    rowSelection = true,
    rowExpandedRender,
    expandedKeys,
    onSelect,
    onSelectAll,
    ...props
}: AppTableProps<T>) => {
    const columns = props.columns.filter(col => !col.hidden);
    const colSpan = useMemo(() => {
        return columns.length + 1 + (rowSelection ? 1 : 0);
    }, [columns.length, rowSelection])
    const checkboxRef = useRef<HTMLInputElement>(null);

    const location = useLocation();
    const navigate = useNavigate();
    const [expandedKeyLoadeds, setExpandedKeyLoadeds] = useState<string[]>([]);

    useEffect(() => {
        setExpandedKeyLoadeds(ex => [...ex, ...(expandedKeys || []).filter(e => !ex.includes(e))])
    }, [expandedKeys])

    useEffect(() => {
        if (checkboxRef.current) {
            if (!appTable.isSelectedAll && appTable.selecteds.length > 0) {
                checkboxRef.current.indeterminate = true;
            }
            else {
                checkboxRef.current.indeterminate = false;
            }
        }
    }, [appTable.isSelectedAll, checkboxRef, appTable.selecteds])

    return <Fragment>
        <div className="bg-white shadow rounded">
            <table className="min-w-full table-auto rounded">
                <thead className="text-left text-white">
                    <tr>
                        {rowSelection && <th className="bg-blue-800 px-4 py-3 w-12 rounded-tl">
                            <input
                                type="checkbox"
                                className="accent-blue-600"
                                onClick={() => {
                                    appTable.handleSelectAll()
                                    onSelectAll?.();
                                }}
                                checked={appTable.isSelectedAll}
                                ref={checkboxRef}
                            />
                        </th>}
                        <th className={`bg-blue-800 px-4 py-3 w-12 ${!rowSelection ? "rounded-tl" : ""}`}>#</th>
                        {columns.map((col, i) => {
                            const sort = appTable.sortData.find(s => s.colKey === col.key);
                            return <Fragment>
                                <th
                                    key={i}
                                    style={{ width: col.width }}
                                    className={`whitespace-nowrap overflow-hidden text-ellipsis bg-blue-800 px-4 py-3 relative text-${col.textAlignHead || col.textAlign || "left"} ${i === columns.length - 1 ? "rounded-tr" : ""}`}
                                >
                                    {col.label}
                                    {col.sort && <span onClick={() => {
                                        const old = appTable.sortData.find(s => s.colKey === col.key);
                                        const _sortData = appTable.sortData.filter(s => s.colKey !== col.key);
                                        let result: Array<{ colKey: string; type: "asc" | "desc" }> = [];
                                        if (old?.type === "desc") {
                                            result = _sortData;
                                        }
                                        else {
                                            result = [
                                                old?.type === "asc" ? { colKey: col.key.toString(), type: "desc" } : { colKey: col.key.toString(), type: "asc" },
                                                ..._sortData
                                            ];
                                        }

                                        const order = result.filter(s => s.type).map(s => `${s.colKey} ${s.type.toLowerCase()}`).join(",");
                                        navigate({
                                            ...location,
                                            search: stringify(
                                                { ...parse(location.search, { ignoreQueryPrefix: true }), order: order === "" ? undefined : order },
                                                { arrayFormat: "brackets" }
                                            )
                                        });
                                    }} className="cursor-pointer ml-2">
                                        {sort?.type === undefined && <i className="fa-solid fa-sort"></i>}
                                        {sort?.type === "asc" && <i className="fa-solid fa-sort-up"></i>}
                                        {sort?.type === "desc" && <i className="fa-solid fa-sort-down"></i>}
                                    </span>}
                                </th>
                            </Fragment>
                        })}
                    </tr>
                </thead>
                <tbody className="text-gray-700">
                    {appTable.dataSource.map((item: any, i) => {
                        let stt: number = i + 1;
                        if (appTable.pagination) {
                            stt = appTable.pagination.pageSize * (appTable.pagination.page - 1) + i + 1
                        }
                        const selected = appTable.selecteds.findIndex(s => s[appTable.rowKey] === item[appTable.rowKey]) >= 0;
                        const tdClass = `px-4 py-3 group-hover/row:bg-gray-50 ${selected ? "bg-blue-50 group-hover/row:bg-blue-50" : ""}`;

                        const rowClass = `${props.rowClassName?.(item) || ""} border-t group/row`;
                        const cellClass = `${props.cellClassName?.(item) || ""}`;

                        const colSpan = columns.length + 1 + (rowSelection ? 1 : 0);
                        return <Fragment>
                            <tr className={rowClass}>
                                {rowSelection && <td className={`${tdClass} ${cellClass}`}>
                                    <input
                                        type="checkbox"
                                        className="rowCheckbox accent-blue-600"
                                        onClick={() => {
                                            onSelect?.(item);
                                            appTable.handleSelect(item)
                                        }}
                                        checked={selected}
                                    />
                                </td>}
                                <td className={`${tdClass} ${cellClass}`}>{stt}</td>
                                {columns.map((col, j) => {
                                    const rowKey = col.key as keyof T;
                                    const _item = item as any;
                                    const value = (col.render && col.render(item)) || _item[rowKey];
                                    return <td key={`${i}-${j}`} style={{ width: col.width }} className={`${tdClass} text-${col.textAlignBody || col.textAlign || "left"} ${cellClass}`} >{value}</td>
                                })}
                            </tr>
                            {rowExpandedRender && expandedKeyLoadeds.includes(`${item.id}`) && <tr className={`${rowClass} ${expandedKeys?.includes(`${item.id}`) ? "" : "hidden"}`}>
                                <td colSpan={colSpan} className={`${tdClass} ${cellClass}`} >{rowExpandedRender(item, i, stt)}</td>
                            </tr>}
                        </Fragment>
                    })}
                    {appTable.dataSource.length === 0 && <tr className="border-t">
                        <td className="px-4 py-3 text-center" colSpan={colSpan}>
                            {/* Không có dữ liệu */}
                            <AppEmpty />
                        </td>
                    </tr>}
                </tbody>
            </table>
        </div>
        {appTable.pagination && <AdminPagination {...appTable.pagination} />}
    </Fragment >
}

export default AppTable;